import type { FileResponseV2 } from '@emma/api_client/types';
import { Box, Flex, IconButton, Switch, Text, Tooltip } from '@lego/klik-ui';
import { Download, Trash } from '@lego/klik-ui/icons';
import { type MouseEvent, useState } from 'react';
import { CopyableText } from '@/components/common/CopyableText';
import { UserNameLink } from '@/components/common/UserNameLink';
import { FileStatusIcon } from '@/components/file/icons/FileStatusIcon';
import { dateTimeFormat } from '@/utility/date';
import { createColumnHelper } from '../appTable';

const FileToggle = ({
  fileId,
  isDisabled,
  onToggle,
}: {
  fileId: number;
  isDisabled: boolean;
  onToggle: (fileId: number, isDisabled: boolean) => Promise<unknown>;
}) => {
  const [loading, setLoading] = useState(false);
  const checked = !isDisabled;
  const label = checked ? 'Enabled. Click to disable.' : 'Disabled. Click to enable.';

  const handleChange = () => {
    setLoading(true);
    void onToggle(fileId, !isDisabled)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  return (
    <Tooltip label={label} placement="right">
      <Box data-interactive display="inline-block" onClick={(e: MouseEvent) => e.stopPropagation()}>
        <Switch isChecked={checked} isDisabled={loading} onChange={handleChange} />
      </Box>
    </Tooltip>
  );
};

const columnHelper = createColumnHelper<FileResponseV2>();

export const fileTableColumns = (
  onDelete?: (fileId: number) => Promise<void>,
  onDownload?: (fileId: number, fileName: string) => Promise<void>,
  onToggleDisabled?: (fileId: number, isDisabled: boolean) => Promise<unknown>
) =>
  columnHelper.columns([
    columnHelper.accessor('id', {
      cell: (info) => (
        <Text fontFamily="mono" fontSize="sm">
          {info.getValue()}
        </Text>
      ),
      header: 'ID',
      meta: { filterVariant: { constraint: /^[0-9]*$/, type: 'singleSearch' } },
    }),
    columnHelper.accessor('fileName', {
      cell: (info) => info.getValue(),
      header: 'Filename',
      meta: { filterVariant: { type: 'singleSearch' } },
    }),
    columnHelper.accessor('label', {
      cell: (info) => info.getValue(),
      header: 'Label',
      meta: { filterVariant: { type: 'singleSearch' } },
    }),
    columnHelper.accessor('digest', {
      cell: (info) => (
        <CopyableText
          copyLabel="Copy full value"
          prefixLength={8}
          suffixLength={8}
          textProps={{ fontFamily: 'mono', fontSize: 'sm' }}
          truncationMode="prefixSuffix"
          value={info.getValue()}
        />
      ),
      enableSorting: false,
      header: 'Digest',
    }),
    columnHelper.accessor('createdByOid', {
      cell: (info) => <UserNameLink oid={info.getValue()} />,
      enableSorting: false,
      header: 'Created By',
    }),
    columnHelper.accessor('createdAt', {
      cell: (info) => new Date(info.getValue()).toLocaleString(dateTimeFormat.local),
      header: 'Created At',
      meta: { filterVariant: { type: 'dateRange' } },
    }),
    columnHelper.accessor('updatedByOid', {
      cell: (info) => <UserNameLink oid={info.getValue()} />,
      enableSorting: false,
      header: 'Updated By',
    }),
    columnHelper.accessor('updatedAt', {
      cell: (info) => new Date(info.getValue()).toLocaleString(dateTimeFormat.local),
      header: 'Updated At',
      meta: { filterVariant: { type: 'dateRange' } },
    }),
    columnHelper.accessor('isDisabled', {
      cell: (info) => {
        const isDisabled = info.getValue();
        if (onToggleDisabled) {
          return (
            <FileToggle
              fileId={info.row.original.id}
              isDisabled={isDisabled}
              onToggle={onToggleDisabled}
            />
          );
        }
        return <FileStatusIcon isDisabled={isDisabled} />;
      },
      header: 'File Status',
      meta: {
        filterVariant: {
          options: [
            { filterValue: true, name: 'Disabled' },
            { filterValue: false, name: 'Not disabled' },
          ],
          type: 'singleSelect',
        },
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      size: 100,
      meta: { isSticky: true },
      cell: ({ row }) => (
        <Flex gap={1}>
          {onDownload && (
            <IconButton
              aria-label="Download file"
              icon={<Download />}
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                void onDownload(row.original.id, row.original.fileName);
              }}
              size="sm"
              variant="ghost"
            />
          )}
          {onDelete && (
            <IconButton
              aria-label="Delete file"
              icon={<Trash />}
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                void onDelete(row.original.id);
              }}
              size="sm"
              variant="ghost"
            />
          )}
        </Flex>
      ),
    }),
  ]);
